/**
 * Operator portal rework PR 1 — the operator's portal, managed by Paelode
 * (lx-coord/operator-portal-design.md §2.1). Operator self-signup is closed; this card is where
 * the company is set up and its users are added, re-invited, re-roled or suspended.
 *
 * Admins and foremen get an email with a temporary password (the web portal signs in with email
 * and password). A pumper needs a phone and is phone-first (D7, Corbin 2026-10-18): the invite has
 * no password; they sign in to the app with their phone and set a web password with "Forgot
 * password". "Send invite" on a pumper does the same again.
 *
 * PR 5 — "View as" opens the portal in a new tab as an active user, read-only (enforced by the
 * API), for 30 minutes, with a reason. "Recent views" is the Paelode-side audit of those sessions.
 */
import * as React from 'react';
import { toast } from 'sonner';
import { Button } from 'src/components/ui/button';
import { FormRow, Pill, fieldStyle, labelStyle, subText } from '../../ui/bits';
import { apiErrorText, useApiOpts } from '@lodex-pro/web-shared';
import {
  OPERATOR_TIER_OPTIONS,
  importOperatorUsers,
  useRefreshOperatorUsers,
  sendPendingInvites,
  type InviteRunTotals,
  type ImportResponse,
  type ImportRow,
  OPERATOR_USER_ROLE_OPTIONS,
  operatorPortalErrorMessage,
  useCreateOperatorCompany,
  useCreateOperatorUser,
  useEndViewAs,
  useOperatorCompany,
  useOperatorCompanyUsers,
  useResendOperatorInvite,
  useClearOperatorUserEmailFlag,
  useStartViewAs,
  useUpdateOperatorUser,
  useViewAsSessions,
  type OperatorCompany,
  type OperatorCompanyUser,
  type OperatorTierOption,
  type OperatorUserRole,
  type ViewAsSessionRow,
} from './portal-hooks';
import { OperatorPlanCard } from './OperatorPlanCard';
import { OperatorApiKeysCard } from './OperatorApiKeysCard';
import { OperatorMqttGatewaysCard } from './OperatorMqttGatewaysCard';
import { OperatorTanksCard } from './OperatorTanksCard';
import { formatNumber } from '@lodex-pro/shared/number-format';

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-bg-secondary)',
  borderRadius: 'var(--radius-card)',
  padding: '20px',
  marginBottom: '16px',
};
const primaryButton: React.CSSProperties = {
  backgroundColor: 'var(--color-accent-blue)',
  color: 'var(--color-on-accent)',
};

/** OPP PR 11b — the hover text on a pumper's phone pill. */
const PHONE_CONFIRMED_TITLE =
  'Set when the pumper signs in to the mobile app with a text-message code sent to this ' +
  'number. For information only: nothing requires it.';

function SetUpCompany({
  platformOperatorId,
  operatorName,
}: {
  platformOperatorId: string;
  operatorName: string;
}): React.JSX.Element {
  const [tier, setTier] = React.useState<OperatorTierOption>('BASIC');
  const create = useCreateOperatorCompany(platformOperatorId);
  return (
    <div>
      <p style={{ ...subText, textTransform: 'none', margin: '0 0 12px' }}>
        {operatorName} has no portal yet. Setting it up creates the company; then add its users
        below.
      </p>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '12px' }}>
        <FormRow label="Plan" htmlFor="portal-tier">
          <select
            id="portal-tier"
            value={tier}
            onChange={(e): void => setTier(e.target.value as OperatorTierOption)}
            style={{ ...fieldStyle, maxWidth: '180px' }}
          >
            {OPERATOR_TIER_OPTIONS.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </FormRow>
        <Button
          type="button"
          disabled={create.isPending}
          style={primaryButton}
          onClick={(): void =>
            create.mutate(
              { platformOperatorId, tier },
              {
                onSuccess: () => toast.success('Portal set up'),
                onError: (err) => toast.error(operatorPortalErrorMessage(err)),
              },
            )
          }
        >
          {create.isPending ? 'Setting up…' : 'Set up portal'}
        </Button>
      </div>
    </div>
  );
}

function AddUserForm({ company }: { company: OperatorCompany }): React.JSX.Element {
  const [fullName, setFullName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [role, setRole] = React.useState<OperatorUserRole>('PUMPER');
  const [error, setError] = React.useState<string | null>(null);
  const create = useCreateOperatorUser(company.id);

  const submit = (): void => {
    if (fullName.trim() === '' || email.trim() === '') {
      setError('Name and email are required.');
      return;
    }
    if (role === 'PUMPER' && phone.trim() === '') {
      setError('A pumper needs a phone number.');
      return;
    }
    setError(null);
    create.mutate(
      {
        fullName: fullName.trim(),
        email: email.trim(),
        ...(phone.trim() !== '' ? { phone: phone.trim() } : {}),
        role,
      },
      {
        onSuccess: (res) => {
          setFullName('');
          setEmail('');
          setPhone('');
          if (res.inviteEmailed) toast.success('User added and invited');
          else toast.warning('User added, but the invite email did not go out. Use “Send invite”.');
        },
        onError: (err) => setError(operatorPortalErrorMessage(err)),
      },
    );
  };

  return (
    <div style={{ marginTop: '16px' }}>
      <h4 style={{ fontSize: '13px', fontWeight: 600, margin: '0 0 10px' }}>Add a user</h4>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <FormRow label="Name" htmlFor="portal-user-name" required>
          <input
            id="portal-user-name"
            value={fullName}
            onChange={(e): void => setFullName(e.target.value)}
            style={fieldStyle}
          />
        </FormRow>
        <FormRow label="Email" htmlFor="portal-user-email" required>
          <input
            id="portal-user-email"
            type="email"
            value={email}
            onChange={(e): void => setEmail(e.target.value)}
            style={fieldStyle}
          />
        </FormRow>
        <FormRow
          label="Phone"
          htmlFor="portal-user-phone"
          required={role === 'PUMPER'}
          hint="10-digit US number. Required for a pumper."
        >
          <input
            id="portal-user-phone"
            type="tel"
            value={phone}
            onChange={(e): void => setPhone(e.target.value)}
            style={fieldStyle}
          />
        </FormRow>
        <FormRow label="User type" htmlFor="portal-user-role" required>
          <select
            id="portal-user-role"
            value={role}
            onChange={(e): void => setRole(e.target.value as OperatorUserRole)}
            style={fieldStyle}
          >
            {OPERATOR_USER_ROLE_OPTIONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </FormRow>
      </div>
      {error !== null && (
        <p role="alert" style={{ ...labelStyle, color: 'var(--color-danger)', marginTop: '10px' }}>
          {error}
        </p>
      )}
      <div style={{ marginTop: '12px' }}>
        <Button type="button" disabled={create.isPending} style={primaryButton} onClick={submit}>
          {create.isPending ? 'Adding…' : 'Add and send invite'}
        </Button>
      </div>
    </div>
  );
}

function UserRow({
  tenantId,
  user,
}: {
  tenantId: string;
  user: OperatorCompanyUser;
}): React.JSX.Element {
  const update = useUpdateOperatorUser(tenantId, user.userId);
  const resend = useResendOperatorInvite(tenantId, user.userId);
  const clearFlag = useClearOperatorUserEmailFlag(tenantId, user.userId);
  const startViewAs = useStartViewAs(tenantId);
  const [reason, setReason] = React.useState<string | null>(null);
  const busy = update.isPending || resend.isPending || startViewAs.isPending;
  const suspended = user.status !== 'ACTIVE';
  const onError = (err: unknown): void => {
    toast.error(operatorPortalErrorMessage(err));
  };
  const who = user.fullName ?? user.email ?? 'this user';

  const openPortal = (): void => {
    const why = (reason ?? '').trim();
    if (why.length < 10) {
      toast.error('Say why, in at least 10 characters. It is kept in the audit.');
      return;
    }
    // Open the tab inside the click (a later window.open is a blocked popup), then point it at
    // the portal link once the session exists.
    const tab = window.open('', '_blank');
    if (tab !== null) tab.opener = null;
    startViewAs.mutate(
      { operatorTenantId: tenantId, targetUserId: user.userId, reason: why },
      {
        onSuccess: (res) => {
          if (tab !== null) tab.location.href = res.portalUrl;
          else window.location.assign(res.portalUrl);
          setReason(null);
          toast.success(`Viewing the portal as ${who} for 30 minutes, read-only`);
        },
        onError: (err) => {
          tab?.close();
          onError(err);
        },
      },
    );
  };

  return (
    <>
      <tr style={{ borderBottom: '1px solid var(--color-border-secondary)' }}>
        <td style={{ padding: '8px 6px' }}>
          <div style={{ fontWeight: 500 }}>{user.fullName ?? '—'}</div>
          <div style={{ ...subText, textTransform: 'none' }}>
            {user.email ?? '—'}
            {user.phone !== null && ` · ${user.phone}`}
          </div>
        </td>
        <td style={{ padding: '8px 6px' }}>
          <select
            aria-label={`User type for ${user.fullName ?? user.email ?? 'user'}`}
            value={user.role}
            disabled={busy}
            onChange={(e): void =>
              update.mutate(
                { role: e.target.value as OperatorUserRole },
                { onSuccess: () => toast.success('User type changed'), onError },
              )
            }
            style={{ ...fieldStyle, maxWidth: '140px' }}
          >
            {OPERATOR_USER_ROLE_OPTIONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </td>
        <td style={{ padding: '8px 6px' }}>
          {suspended ? (
            <Pill label="Suspended" tone="danger" />
          ) : !user.invited ? (
            // OPP PR 2 — an imported person has no sign-in account until an invite goes out.
            <Pill label="Imported, not invited" tone="muted" />
          ) : user.activatedAt !== null ? (
            <Pill label="Signed in" tone="positive" />
          ) : (
            <Pill label="Invited" tone="warning" />
          )}
          {/* OPP PR 11b (#897 LOW2): a pumper's phone is unproven until their first SMS sign-in. */}
          {!suspended && user.role === 'PUMPER' && user.phone !== null && (
            // OPP PR 11b (#915 review LOW2): what the stamp means, and that nothing depends
            // on it. The app reports it after a text-code sign-in; it is not checked here.
            <div style={{ marginTop: '4px' }} title={PHONE_CONFIRMED_TITLE}>
              {user.phoneConfirmedAt != null ? (
                <Pill label="Phone confirmed in the app" tone="positive" />
              ) : (
                <Pill label="Not confirmed in the app yet" tone="muted" />
              )}
            </div>
          )}
          {/* 0227 — SES flagged the address; no invite goes to it. */}
          {user.emailStatus !== undefined && user.emailStatus !== 'OK' && (
            <>
              {' '}
              <Pill
                label={user.emailStatus === 'COMPLAINED' ? 'Marked as spam' : 'Email bouncing'}
                tone="danger"
              />
              {/* #936 review L1: Paelode clears a bounce once the address works again; never a
                  complaint, which is the person's own opt-out. */}
              {user.emailStatus === 'BOUNCING' && (
                <>
                  {' '}
                  <Button
                    type="button"
                    variant="outline"
                    disabled={clearFlag.isPending}
                    onClick={(): void => {
                      if (
                        !window.confirm(
                          `Clear the bounce flag for ${user.email ?? 'this user'}? Do this only once the address works again; emails to it start again.`,
                        )
                      ) {
                        return;
                      }
                      clearFlag.mutate(
                        {},
                        {
                          onSuccess: () => toast.success('Bounce flag cleared'),
                          onError,
                        },
                      );
                    }}
                  >
                    Clear flag
                  </Button>
                </>
              )}
            </>
          )}
        </td>
        <td style={{ padding: '8px 6px', whiteSpace: 'nowrap' }}>
          {!suspended && (
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={(): void =>
                resend.mutate(
                  {},
                  {
                    onSuccess: (res) =>
                      res.inviteEmailSkipped === 'EMAIL_BOUNCING' && res.accountReady === true
                        ? toast.warning(
                            `${user.fullName ?? 'The pumper'} can sign in to the app with their phone. No email went: ${user.email ?? 'their address'} is bouncing or was marked as spam.`,
                          )
                        : res.inviteEmailSkipped === 'EMAIL_BOUNCING'
                          ? toast.error(
                              `Email to ${user.email ?? 'this user'} is bouncing or was marked as spam, so nothing was sent.`,
                            )
                          : res.inviteEmailed
                            ? toast.success(`Invite sent to ${user.email ?? 'the user'}`)
                            : toast.warning('The invite email did not go out. Try again.'),
                    onError,
                  },
                )
              }
            >
              Send invite
            </Button>
          )}{' '}
          <Button
            type="button"
            variant="outline"
            disabled={busy}
            onClick={(): void =>
              update.mutate(
                { status: suspended ? 'ACTIVE' : 'SUSPENDED' },
                {
                  onSuccess: () => toast.success(suspended ? 'User reactivated' : 'User suspended'),
                  onError,
                },
              )
            }
          >
            {suspended ? 'Reactivate' : 'Suspend'}
          </Button>
          {!suspended && (
            <>
              {' '}
              <Button
                type="button"
                variant="outline"
                disabled={busy}
                onClick={(): void => setReason(reason === null ? '' : null)}
              >
                View as
              </Button>
            </>
          )}
        </td>
      </tr>
      {reason !== null && (
        <tr>
          <td colSpan={4} style={{ padding: '4px 6px 12px' }}>
            <label htmlFor={`view-as-reason-${user.userId}`} style={labelStyle}>
              Why are you viewing the portal as {who}?
            </label>
            <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
              <input
                className="keep-case"
                id={`view-as-reason-${user.userId}`}
                value={reason}
                maxLength={500}
                onChange={(e): void => setReason(e.target.value)}
                placeholder="e.g. Customer reports a missing ticket on 10/14"
                style={fieldStyle}
              />
              <Button type="button" disabled={busy} style={primaryButton} onClick={openPortal}>
                Open portal
              </Button>
              <Button type="button" variant="outline" onClick={(): void => setReason(null)}>
                Cancel
              </Button>
            </div>
            <p style={{ ...subText, textTransform: 'none', margin: '6px 0 0' }}>
              Read-only, for 30 minutes. The operator does not see this; Paelode keeps who, when,
              why and what was read.
            </p>
          </td>
        </tr>
      )}
    </>
  );
}

function fmt(iso: string | null): string {
  return iso === null ? '—' : new Date(iso).toLocaleString();
}

function ViewAsRow({
  tenantId,
  row,
}: {
  tenantId: string;
  row: ViewAsSessionRow;
}): React.JSX.Element {
  const end = useEndViewAs(tenantId, row.id);
  return (
    <tr style={{ borderBottom: '1px solid var(--color-border-secondary)' }}>
      <td style={{ padding: '6px' }}>{fmt(row.startedAt)}</td>
      <td style={{ padding: '6px' }}>{row.adminName ?? row.adminEmail ?? '—'}</td>
      <td style={{ padding: '6px' }}>{row.targetName ?? row.targetEmail ?? '—'}</td>
      <td style={{ padding: '6px' }}>{row.reason}</td>
      <td style={{ padding: '6px' }}>{formatNumber(row.readCount)}</td>
      <td style={{ padding: '6px', whiteSpace: 'nowrap' }}>
        {row.status === 'LIVE' ? (
          <Button
            type="button"
            variant="outline"
            disabled={end.isPending}
            onClick={(): void =>
              end.mutate(
                {},
                {
                  onSuccess: () => toast.success('View ended'),
                  onError: (err) => toast.error(operatorPortalErrorMessage(err)),
                },
              )
            }
          >
            End now
          </Button>
        ) : row.status === 'EXPIRED' ? (
          <Pill label="Expired" tone="neutral" />
        ) : (
          <Pill label={`Ended ${fmt(row.endedAt)}`} tone="neutral" />
        )}
      </td>
    </tr>
  );
}

/** PR 5 — the Paelode-side audit of view-as sessions on this company. */
function RecentViews({ tenantId }: { tenantId: string }): React.JSX.Element | null {
  const { data } = useViewAsSessions(tenantId);
  const sessions = data?.sessions ?? [];
  if (sessions.length === 0) return null;
  return (
    <div style={{ marginTop: '20px' }}>
      <h4 style={{ fontSize: '13px', fontWeight: 600, margin: '0 0 8px' }}>Recent views</h4>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
        <thead>
          <tr style={{ textAlign: 'left' }}>
            <th style={{ ...labelStyle, padding: '6px' }}>Started</th>
            <th style={{ ...labelStyle, padding: '6px' }}>By</th>
            <th style={{ ...labelStyle, padding: '6px' }}>As</th>
            <th style={{ ...labelStyle, padding: '6px' }}>Reason</th>
            <th style={{ ...labelStyle, padding: '6px' }}>Reads</th>
            <th style={{ ...labelStyle, padding: '6px' }} aria-label="Status" />
          </tr>
        </thead>
        <tbody>
          {sessions.map((s) => (
            <ViewAsRow key={s.id} tenantId={tenantId} row={s} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PendingInvites({
  tenantId,
  count,
}: {
  tenantId: string;
  count: number;
}): React.JSX.Element | null {
  const opts = useApiOpts();
  const refresh = useRefreshOperatorUsers(tenantId);
  const [running, setRunning] = React.useState(false);
  const [progress, setProgress] = React.useState<InviteRunTotals | null>(null);
  if (count === 0 && !running) return null;

  const run = async (): Promise<void> => {
    setRunning(true);
    setProgress(null);
    try {
      const t = await sendPendingInvites(opts, tenantId, setProgress);
      // 0227 — people whose address SES flagged are never invited; say how many.
      const bouncing =
        (t.emailBouncing > 0
          ? ` ${String(t.emailBouncing)} skipped: their email is bouncing.`
          : '') +
        (t.phoneOnly > 0
          ? ` ${String(t.phoneOnly)} pumper(s) set up for the app only: their email is bouncing.`
          : '');
      if (
        t.failed === 0 &&
        t.emailFailed === 0 &&
        t.pending === 0 &&
        t.emailBouncing === 0 &&
        t.phoneOnly === 0
      ) {
        toast.success(`Invites sent to ${String(t.invited)}`);
      } else {
        toast.warning(
          `Invited ${String(t.invited)}; ${String(t.failed)} could not be invited, ${String(t.emailFailed)} invite email(s) did not go out, ${String(t.pending)} still not invited.${bouncing}`,
        );
      }
    } catch (err) {
      toast.error(operatorPortalErrorMessage(err));
    } finally {
      setRunning(false);
      refresh();
    }
  };

  return (
    <div
      data-testid="pending-invites"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        padding: '10px 12px',
        margin: '0 0 12px',
        borderRadius: '12px',
        backgroundColor: 'var(--color-bg-tertiary)',
      }}
    >
      <span style={{ fontSize: '13px' }}>
        {running && progress !== null
          ? `Sending invites… ${String(progress.invited)} sent, ${String(progress.pending)} to go.`
          : `${count === 1 ? '1 person is' : `${formatNumber(count)} people are`} imported but not invited: they cannot sign in until an invite goes out.`}
      </span>
      <Button
        type="button"
        disabled={running}
        style={primaryButton}
        onClick={(): void => void run()}
      >
        {running ? 'Sending…' : `Send invites (${formatNumber(count)})`}
      </Button>
    </div>
  );
}

const STATUS_LABEL: Record<ImportRow['status'], string> = {
  NEW: 'New',
  UPDATE: 'Update',
  ROLE_CHANGE: 'Type change',
  UNCHANGED: 'No change',
  ERROR: 'Not imported',
};

function ImportUsers({ company }: { company: OperatorCompany }): React.JSX.Element {
  const opts = useApiOpts();
  const refresh = useRefreshOperatorUsers(company.id);
  const [file, setFile] = React.useState<File | null>(null);
  const [preview, setPreview] = React.useState<ImportResponse | null>(null);
  const [ticked, setTicked] = React.useState<ReadonlySet<string>>(new Set());
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const run = async (apply: boolean): Promise<void> => {
    if (file === null) return;
    setBusy(true);
    setError(null);
    try {
      const res = await importOperatorUsers(opts, company.id, file, {
        apply,
        ...(apply ? { roleChanges: [...ticked] } : {}),
      });
      if (apply) {
        const r = res.result;
        toast.success(
          `Imported: ${String(r?.created ?? 0)} new, ${String(r?.updated ?? 0)} updated, ${String(r?.rolesChanged ?? 0)} type change(s). Invites have not been sent.`,
        );
        setPreview(null);
        setFile(null);
        setTicked(new Set());
        refresh();
      } else {
        setPreview(res);
        setTicked(new Set());
      }
    } catch (err) {
      setError(apiErrorText(err, 'The file could not be read.'));
    } finally {
      setBusy(false);
    }
  };

  const s = preview?.summary;
  const writable = s === undefined ? 0 : s.new + s.update + ticked.size;
  return (
    <div style={{ marginTop: '20px' }} data-testid="import-users">
      <h4 style={{ fontSize: '13px', fontWeight: 600, margin: '0 0 6px' }}>Import users</h4>
      <p style={{ ...subText, textTransform: 'none', margin: '0 0 10px' }}>
        A CSV or XLSX with name, email, phone and user type (pumper, foreman or admin). Nothing is
        saved until you import, and nobody is emailed until you send invites.
      </p>
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
        <input
          aria-label="User file"
          type="file"
          accept=".csv,.xlsx"
          onChange={(e): void => {
            setFile(e.target.files?.[0] ?? null);
            setPreview(null);
            setError(null);
          }}
        />
        <Button
          type="button"
          variant="outline"
          disabled={file === null || busy}
          onClick={(): void => void run(false)}
        >
          Check file
        </Button>
      </div>
      {error !== null && (
        <p role="alert" style={{ ...labelStyle, color: 'var(--color-danger)', marginTop: '10px' }}>
          {error}
        </p>
      )}
      {preview !== null && s !== undefined && (
        <div style={{ marginTop: '12px' }}>
          <p style={{ fontSize: '13px', margin: '0 0 8px' }} data-testid="import-summary">
            {s.new} new · {s.update} update · {s.roleChange} type change · {s.unchanged} no change ·{' '}
            {s.errors} not imported
          </p>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ textAlign: 'left' }}>
                <th style={{ ...labelStyle, padding: '4px' }}>Row</th>
                <th style={{ ...labelStyle, padding: '4px' }}>Person</th>
                <th style={{ ...labelStyle, padding: '4px' }}>Result</th>
              </tr>
            </thead>
            <tbody>
              {preview.rows.map((r) => (
                <tr
                  key={r.rowNumber}
                  style={{ borderTop: '1px solid var(--color-border-secondary)' }}
                >
                  <td style={{ padding: '4px' }}>{r.rowNumber}</td>
                  <td style={{ padding: '4px' }}>
                    {r.fullName || '—'} <span style={subText}>{r.email}</span>
                  </td>
                  <td style={{ padding: '4px' }}>
                    <Pill
                      label={STATUS_LABEL[r.status]}
                      tone={
                        r.status === 'ERROR'
                          ? 'danger'
                          : r.status === 'NEW'
                            ? 'positive'
                            : 'neutral'
                      }
                    />{' '}
                    {r.status === 'ROLE_CHANGE' ? (
                      <label style={{ fontSize: '12px' }}>
                        <input
                          type="checkbox"
                          aria-label={`Change ${r.email} to ${r.role ?? ''}`}
                          checked={ticked.has(r.email)}
                          onChange={(e): void => {
                            const next = new Set(ticked);
                            if (e.target.checked) next.add(r.email);
                            else next.delete(r.email);
                            setTicked(next);
                          }}
                        />{' '}
                        {r.detail}
                      </label>
                    ) : (
                      r.detail !== undefined && <span style={{ fontSize: '12px' }}>{r.detail}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: '10px' }}>
            <Button
              type="button"
              style={primaryButton}
              disabled={busy || writable === 0}
              onClick={(): void => void run(true)}
            >
              {busy ? 'Importing…' : `Import ${String(writable)}`}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function CompanyUsers({ company }: { company: OperatorCompany }): React.JSX.Element {
  const { data, isLoading, isError } = useOperatorCompanyUsers(company.id);
  const users = data?.users ?? [];
  // 0227 — a flagged address is never invited, so it is not counted as waiting for one.
  // #936 review MED-1: a phone-first pumper is invited all the same (they sign in by text code).
  const pending = users.filter(
    (u) =>
      !u.invited &&
      u.status === 'ACTIVE' &&
      ((u.emailStatus ?? 'OK') === 'OK' || (u.role === 'PUMPER' && u.phone !== null)),
  ).length;
  return (
    <div>
      <p style={{ ...subText, textTransform: 'none', margin: '0 0 12px' }}>
        {company.name} · plan {company.tier}
      </p>
      <PendingInvites tenantId={company.id} count={pending} />
      {isLoading ? (
        <p style={subText}>Loading users…</p>
      ) : isError ? (
        <p role="alert" style={{ ...labelStyle, color: 'var(--color-danger)' }}>
          Could not load the users.
        </p>
      ) : users.length === 0 ? (
        <p style={{ ...subText, textTransform: 'none' }}>No users yet.</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
          <thead>
            <tr style={{ textAlign: 'left' }}>
              <th style={{ ...labelStyle, padding: '6px' }}>User</th>
              <th style={{ ...labelStyle, padding: '6px' }}>Type</th>
              <th style={{ ...labelStyle, padding: '6px' }}>Status</th>
              <th style={{ ...labelStyle, padding: '6px' }} aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <UserRow key={u.userId} tenantId={company.id} user={u} />
            ))}
          </tbody>
        </table>
      )}
      <AddUserForm company={company} />
      <RecentViews tenantId={company.id} />
      <ImportUsers company={company} />
    </div>
  );
}

/** OPP PR 6 — the portal card, then (once there is a company) its Plan card. */
export function OperatorPortalCard({
  platformOperatorId,
  operatorName,
}: {
  platformOperatorId: string;
  operatorName: string;
}): React.JSX.Element {
  const { data, isLoading } = useOperatorCompany(platformOperatorId);
  const company = data?.company ?? null;
  return (
    <>
      <section style={cardStyle} data-testid="operator-portal-card">
        <h3 style={{ fontSize: '14px', fontWeight: 600, margin: '0 0 14px' }}>Operator portal</h3>
        {isLoading ? (
          <p style={subText}>Loading…</p>
        ) : company === null ? (
          <SetUpCompany platformOperatorId={platformOperatorId} operatorName={operatorName} />
        ) : (
          <CompanyUsers company={company} />
        )}
      </section>
      {company !== null && <OperatorPlanCard tenantId={company.id} />}
      {/* Operator portal rework PR 9 (0213) — the company's API keys. */}
      {company !== null && <OperatorApiKeysCard tenantId={company.id} />}
      {/* 13.11 (0263): the company's MQTT gateways, provisioned by Paelode only (D-12). */}
      {company !== null && <OperatorMqttGatewaysCard tenantId={company.id} />}
      {/* Tank registry PR 2 — the company's tank registry, kept by Paelode on its behalf. */}
      {company !== null && <OperatorTanksCard tenantId={company.id} />}
    </>
  );
}
